// src/pages/data-center/components/Detail.tsx

import {
  useAddDataToTable,
  useAddNewColumnInTable,
  useDeleteMultipleRowsData,
  useGetTableById,
  useGetTableDataInfinite,
  useUpdateRowData,
} from "@/queries/data-center/data-center.api";
import {
  Avatar,
  AvatarFallback,
  Button,
  Heading,
  Icon,
  Input,
  useToast,
} from "@elaniintc/goes-design-system";
import { Skeleton } from "@/components/ui/skeleton";
import React, { useEffect, useMemo, useState } from "react";
import NewColumnModal from "./NewColumnModal";
import Table from "./refactor/Table";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { useDataCenterStore } from "@/stores/data-center/useCenterStore";
import { DataCenterConfirmationModal } from "./confirmation-modal";
import { ConfirmationModalBase } from "@/components/common/modals";
import EditTableModal from "./EditTableModal";
import EditColumnModal from "./EditColumnModal";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import { useNavigate } from "react-router";
import { useDebounceValue } from "usehooks-ts";

interface DetailProps {
  tableId: string;
}

export const Detalle: React.FC<DetailProps> = ({ tableId }) => {
  const { hasPermission } = useAuthorization();
  const canCreateRegistry = hasPermission(
    Permissions["data-center"]["create-registry"],
  );
  const canUpdateTable = hasPermission(Permissions["data-center"]["update"]);
  const canUpdateRegistry = hasPermission(
    Permissions["data-center"]["update-registry"],
  );
  const canDeleteRegistry = hasPermission(
    Permissions["data-center"]["delete-registry"],
  );

  const { data, isLoading, refetch } = useGetTableById(tableId);

  const [search, setSearch] = useState("");
  const [diferredSearch] = useDebounceValue(search, 500);

  const [sortState, setSortState] = useState<{
    columnId: string;
    direction: "asc" | "desc" | "off";
  } | null>(null);

  const {
    data: tableData,
    refetch: refetchTableData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetTableDataInfinite({
    name: data?.data?.name || "",
    search: diferredSearch,
    ...(sortState
      ? {
          order_by: `${
            sortState.direction === "asc" ? "" : "!"
          }${sortState.columnId}`,
        }
      : {}),
  });

  const allData = tableData?.pages.flatMap((p) => p?.data?.data || []);

  const { mutateAsync: addColumns, isPending: isLoadingSave } =
    useAddNewColumnInTable();
  const { mutateAsync: addData, isPending: isLoadingAddData } =
    useAddDataToTable();
  const { mutateAsync: updateRowData, isPending: isLoadingUpdateRowData } =
    useUpdateRowData();
  const {
    mutateAsync: deleteMultipleRowsData,
    isPending: isLoadingDeleteRowData,
  } = useDeleteMultipleRowsData();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { columns } = useDataCenterStore();

  const [selectedRows, setSelectedRows] = useState<
    {
      id: string;
      rowId: string;
    }[]
  >([]);

  const [modalState, setModalState] = useState<
    | "idle"
    | "edit"
    | "new-column"
    | "save"
    | "delete"
    | "edit-table"
    | "edit-column"
  >("idle");

  const methods = useForm<{
    rows: {
      id: string;
      created_at: string;
      updated_at: string;
      [key: string]: string;
    }[];
  }>({
    defaultValues: {
      rows: tableData?.pages.flatMap((p) => p?.data?.data || []),
    },
  });
  const { fields, replace, append } = useFieldArray({
    control: methods.control,
    name: "rows",
    keyName: "row-key",
  });

  useEffect(() => {
    if (tableData?.pages) {
      setSelectedRows([]);
      setAreAllRowsSelected(false);
      replace(tableData.pages.flatMap((p) => p?.data?.data || []));
    }
  }, [tableData, replace]);

  const appendNewRow = () => {
    append({
      id: "",
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      ...Object.fromEntries(
        data?.data?.columns.map((col: { name: string }) => {
          return { [col.name]: "" };
        }) || [],
      ),
    });
  };

  const onAddNewData = async (
    columnId: string,
    value: string | boolean | number,
  ) => {
    try {
      await addData({
        table_name: data?.data?.name || "",
        data: {
          [columnId]: value,
        },
      });
      refetchTableData();
      showToast({
        title: "Cambios guardados",
        description:
          "Los cambios realizados en la tabla se guardaron correctamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al agregar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const onEditData = async (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => {
    try {
      await updateRowData({
        id: registerId,
        table_name: data?.data?.name || "",
        data: {
          [columnId]: value,
        },
      });
      refetchTableData();
      showToast({
        title: "Cambios guardados",
        description:
          "Los cambios realizados en la tabla se guardaron correctamente.",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (e) {
      const error = e as Error;
      showToast({
        title: "Error al editar registro",
        description: error.message,
        options: {
          type: "error",
          duration: 5000,
        },
      });
    }
  };

  const onDeleteRowsData = async () => {
    try {
      await deleteMultipleRowsData({
        table_name: data?.data?.name || "",
        ids: selectedRows.filter((row) => row.id).map((row) => row.id),
      });
      refetchTableData();
      setSelectedRows([]);
      setAreAllRowsSelected(false);
      setModalState("idle");
      showToast({
        title: "Registros eliminados",
        description: "Los registros han sido eliminados exitosamente.",
        options: {
          type: "success",
          duration: 5000,
        },
      });
    } catch (e) {
      const error = e as Error;
      console.error("Error deleting rows:", error);
      showToast({
        title: "Error al eliminar registros",
        description:
          "No se pudieron eliminar los registros seleccionados. Si ya no existen, alguien más los eliminó.",
        options: {
          type: "error",
          duration: 5000,
        },
      });
      setModalState("idle");
      refetchTableData();
    }
  };

  const onAddData = async (
    columnId: string,
    value: string | boolean | number,
    registerId: string,
  ) => {
    if (!registerId) {
      onAddNewData(columnId, value);
      return;
    }
    onEditData(columnId, value, registerId);
  };

  const [areAllRowsSelected, setAreAllRowsSelected] = useState(false);
  const onSelectAllRows = () => {
    setSelectedRows(
      !areAllRowsSelected
        ? filteredRows.map((row) => ({ id: row.id, rowId: row["row-key"] }))
        : [],
    );
    setAreAllRowsSelected(!areAllRowsSelected);
  };

  const onSelectRow = (
    val: { id: string; rowId: string },
    isSelected: boolean,
  ) => {
    if (isSelected) {
      setSelectedRows((prev) => [...prev, val]);
    } else {
      setAreAllRowsSelected(false);
      setSelectedRows((prev) =>
        prev.filter((row) => row.id !== val.id && row.rowId !== val.rowId),
      );
    }
  };

  const onSubmit = async () => {
    if (columns.length > 0) {
      await addColumns({
        id: tableId,
        name: data?.data?.name || "",
        description: data?.data?.description || "",
        columns: [
          ...(data?.data?.columns || []).map(
            (c: {
              id: string;
              name: string;
              type: string;
              display_name: string;
            }) => ({
              id: c.id,
              name: c.name,
              type: c.type,
              display_name: c.display_name || "",
            }),
          ),
          ...columns.map((col) => ({
            name: col.name,
            type: col.type,
            display_name: col.display_name || "",
          })),
        ],
      });
    }
  };

  const hasUnsavedRows = fields.some((row) => {
    return row.id === "";
  });

  const filteredRows = useMemo(() => {
    return fields.map((row, originalIndex) => ({ ...row, originalIndex }));
  }, [fields]);

  const [selectedColumn, setSelectedColumn] = useState<{
    id: string;
    name: string;
    data_type: string;
    display_name: string;
  } | null>(null);

  const onEditColumnAttempt = (columnId: string) => {
    const column = data?.data?.columns.find(
      (col: { id: string }) => col.id === columnId,
    );
    if (!column) return;
    setModalState("edit-column");
    setSelectedColumn({
      id: column.id,
      name: column.name,
      data_type: column.type,
      display_name: column.display_name,
    });
  };

  return (
    <>
      {modalState === "save" && (
        <DataCenterConfirmationModal
          onClose={() => {
            setModalState("idle");
          }}
          onAccept={onSubmit}
          isLoading={isLoadingSave}
          isOpen={modalState === "save"}
          mode="save"
        />
      )}
      {modalState === "new-column" && (
        <NewColumnModal
          open={modalState === "new-column"}
          name={data?.data?.name || ""}
          description={data?.data?.description || ""}
          tableId={tableId}
          onSuccess={() => {
            setModalState("idle");
            refetch();
            refetchTableData();
            showToast({
              title: "Cambios guardados",
              description:
                "Los cambios realizados en la tabla se guardaron correctamente.",
              options: {
                type: "success",
                duration: 5000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="action" shape="square" type="icon">
                      <Icon type="fas" name="save" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
          }}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}
      {modalState === "edit-column" && selectedColumn && (
        <EditColumnModal
          onClose={() => {
            setModalState("idle");
            setSelectedColumn(null);
          }}
          columns={data?.data?.columns || []}
          table={{
            id: data?.data?.id || "",
            name: data?.data?.name || "",
            description: data?.data?.description || "",
            table_display_name: data?.data?.table_display_name || "",
          }}
          onSuccess={() => {
            refetch();
            showToast({
              title: "Cambios guardados",
              description:
                "Los cambios realizados en la tabla se guardaron correctamente.",
              options: {
                type: "success",
                duration: 5000,
                customIcon: (
                  <Avatar size="xs" shape="square">
                    <AvatarFallback color="action" shape="square" type="icon">
                      <Icon type="fas" name="save" />
                    </AvatarFallback>
                  </Avatar>
                ),
              },
            });
            setModalState("idle");
            setSelectedColumn(null);
            refetchTableData();
          }}
          columnData={{
            id: selectedColumn.id,
            data_type: selectedColumn.data_type as
              | "STRING"
              | "INTEGER"
              | "BOOLEAN",
            name: selectedColumn.name,
            display_name: selectedColumn.display_name || "",
          }}
          open={modalState === "edit-column"}
        />
      )}
      {modalState === "delete" && (
        <ConfirmationModalBase
          acceptText="Eliminar registros"
          cancelText="Cancelar"
          description="¿Estás seguro de que deseas eliminar los registros de la lista? Se eliminará la información de todas sus celdas."
          title="Eliminar registros"
          isOpen={modalState === "delete"}
          isLoading={isLoadingDeleteRowData}
          onAccept={onDeleteRowsData}
          onClose={() => {
            setModalState("idle");
          }}
          acceptVariant="danger-secondary"
        />
      )}
      {modalState === "edit-table" && (
        <EditTableModal
          tableData={{
            description: data?.data?.description || "",
            name: data?.data?.name || "",
            id: data?.data?.id || "",
            table_display_name: data?.data?.table_display_name || "",
          }}
          open={modalState === "edit-table"}
          onDeactivate={() => {
            navigate("/centro-de-datos");
          }}
          onSuccess={() => {
            // setIsOpenEditTableModal(false);
            // setSelectedTable(null);
            refetch();
          }}
          onClose={() => {
            setModalState("idle");
          }}
        />
      )}
      <div className="border border-neutral shadow-custom rounded-xl mt-8 flex flex-col h-full overflow-hidden">
        <div className="flex justify-between items-start gap-3 border-b border-neutral w-full p-3">
          <div className="flex justify-start gap-2.5 items-center flex-auto truncate">
            {canUpdateTable && (
              <Button
                size="default"
                variant="quiet"
                onClick={() => {
                  setModalState("edit-table");
                }}
              >
                <Icon type="far" name="edit" className="text-current" />
              </Button>
            )}
            {isLoading ? (
              <div className="flex flex-col">
                <Skeleton className="h-4 w-48 mb-2" />
                <Skeleton className="h-3 w-64" />
              </div>
            ) : (
              <div className="flex flex-col justify-center max-h-10 truncate w-full">
                <Heading
                  variant="h6"
                  className="text-body-loud w-full truncate"
                  title={
                    data?.data?.table_display_name ||
                    data?.data?.name ||
                    "Nombre de la tabla"
                  }
                >
                  {data?.data?.table_display_name ||
                    data?.data?.name ||
                    "Nombre de la tabla"}
                </Heading>
                <p
                  className="text-body-quiet -translate-y-0.75 text-sm w-full truncate"
                  title={data?.data?.description || ""}
                >
                  {data?.data?.description || ""}
                </p>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 h-10">
            <div className="relative">
              <Icon
                type="far"
                name="search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 text-body text-sm"
              />
              <Input
                variant="xs"
                placeholder="Buscar..."
                className="h-8 w-50 pl-8"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canCreateRegistry && (
              <Button
                variant="default"
                size="xs"
                onClick={() => {
                  appendNewRow();
                }}
                disabled={
                  hasUnsavedRows ||
                  isLoadingDeleteRowData ||
                  isLoadingAddData ||
                  isLoadingUpdateRowData ||
                  data?.data?.columns.length === 0
                }
              >
                Nuevo <Icon type="far" name="plus" className="text-current" />
              </Button>
            )}
          </div>
        </div>

        <div className="overflow-y-auto flex-1 h-full max-h-full">
          {data?.data?.columns?.length === 0 && (
            <div className="w-full h-full flex flex-col items-center justify-center">
              <div className="flex flex-col pb-6">
                <Heading
                  variant="h4"
                  className="text-heading-quiet text-center"
                >
                  Aún no tienes registros
                </Heading>
                <p className="text-body-quiet font-normal">
                  Agrega las columnas necesarias para tu nueva tabla
                </p>
              </div>
              {canUpdateTable && (
                <Button
                  type="button"
                  size="default"
                  onClick={() => setModalState("new-column")}
                >
                  Agregar columna
                  <Icon type="far" name="plus" />
                </Button>
              )}
            </div>
          )}
          {data?.data?.columns?.length > 0 && !isLoading && (
            <FormProvider {...methods}>
              <Table
                id={tableId}
                columns={data?.data?.columns || []}
                canEditRegistry={canUpdateRegistry}
                canEditTable={canUpdateTable}
                canCreateRegistry={canCreateRegistry}
                sortState={sortState}
                onSort={(columnId, direction) => {
                  if (direction === "off") {
                    setSortState(null);
                    return;
                  }
                  setSortState({
                    columnId,
                    direction,
                  });
                }}
                rows={
                  (filteredRows as {
                    id: string;
                    "row-key": string;
                    created_at: string;
                    updated_at: string;
                    originalIndex: number;
                    [key: string]: string | number;
                  }[]) || []
                }
                onEditColumn={onEditColumnAttempt}
                selectedRows={selectedRows}
                onNewColumn={() => {
                  setModalState("new-column");
                }}
                shouldAllowNewColumn={
                  allData?.length === 0 &&
                  !isLoadingSave &&
                  !isLoadingAddData &&
                  !isLoadingUpdateRowData &&
                  !isLoadingDeleteRowData
                }
                onAddData={onAddData}
                areAllRowsSelected={areAllRowsSelected}
                onSelectAllRows={onSelectAllRows}
                onSelectRow={onSelectRow}
                hasMoreData={hasNextPage}
                isLoadingMoreData={isFetchingNextPage}
                onLoadMore={fetchNextPage}
              />
            </FormProvider>
          )}
        </div>
        <div className="w-full flex justify-between items-center px-3 bg-neutral-object border-t border-neutral-quiet py-4">
          <p className="text-body text-sm font-normal">
            Mostrando{" "}
            <span className="text-body-loud font-semibold">
              {fields.length}
            </span>{" "}
            filas
          </p>
          {selectedRows.length > 0 && (
            <div className="flex items-center gap-2">
              <p className="text-body font-normal text-sm">
                <span className="text-body-loud font-semibold">
                  {selectedRows.length}
                </span>{" "}
                registro(s) seleccionado(s)
              </p>
              {canDeleteRegistry && (
                <Button
                  variant="danger-tertiary"
                  size="xs"
                  type="button"
                  disabled={
                    isLoadingDeleteRowData ||
                    isLoadingAddData ||
                    isLoadingUpdateRowData
                  }
                  onClick={() => {
                    setModalState("delete");
                  }}
                >
                  Eliminar
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Detalle;
